import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import Constants from '../../utils/Constants';
import { toast } from 'react-toastify';
import StoreX, { store } from '../../redux/oldStore';
import Modal from '../Modal/Modal';
import { Selectable } from '../../utils/CommonTypes/CommonTypes';
import { IState, ReduxMap } from '../../redux/redux';
import { ServerResponse } from '../../utils/Server';
import Editor from '../GenericForms/Editor';
import { FormatDate } from '../../utils/Tools';

type School = {
  id: number;
  name: string;
};

type Tag = {
  Id: number;
  FairId: number;
  Type: 'person' | 'project';
  Value: string;
};

interface IBulkMessageResults {
  IsPreview:boolean;
  ErrorMessages:string[];
  ToList:IMessageResult[];
}

interface IMessageResult {
  Email:string;
  AllowTextMessage:boolean;
  Phone:string;
  SentAtUtc:string;
  Subject:string;
  Body:string;
}

interface IProps extends IState {}

const EmailToolContainer = (props: IProps) => {
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [preEmailEditorState, setPreEmailEditorState] = useState('');
  const [hideSchoolList, setHideSchoolList] = useState(true);
  const [schoolList, setSchoolList] = useState([]);
  const [selectedSchools, setSelectedSchools] = useState<Selectable[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<Selectable[]>([]);
  const [selectedPersonTags, setSelectedPersonTags] = useState<Selectable[]>([]);
  const [selectedProjectTags, setSelectedProjectTags] = useState<Selectable[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<any>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [validFiles, setValidFiles] = useState<any>([]);
  
  const [subjectVal, setSubjectVal] = useState('');
  const [showSendModal, setShowSendModal] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [personTags, setPersonTags] = useState<Tag[]>([]);
  const [projectTags, setProjectTags] = useState<Tag[]>([]);
  const [recipientCategories, setRecipientCategories] = useState([]);
  const [messageResults, setMessageResults] = useState<IBulkMessageResults>();
  const [currentMessage, setCurrentMessage] = useState<number>(0);

  useEffect(() => {
    store.server
      .getApi('../school/list')
      .then((res: any) => {
        setSchoolList(res.Value.schools);
      })
      .catch((err) => {
        console.log(err);
        toast.error(err);
      });

    store.server
      .getApi('../Tag/Info')
      .then((res: any) => {
        console.log(res);
        setPersonTags(res.Value.personTags);
        setProjectTags(res.Value.projectTags);
      })
      .catch((err) => {
        console.log(err);
        toast.error(err);
      });
  }, []);

  useEffect(() => {
    if (props.User) {
      let recipientCategories = [
        { value: 'students', label: props.Settings?.text?.ParticipantLabel ?? 'Students' },
        { value:'studentsTeachers', label: `Copy Teachers of ${props.Settings?.text?.ParticipantLabel ?? 'Students'}`},
        { value: 'parents', label: 'Parents' },
        { value: 'teachers', label: 'Teachers' },
        { value: 'studentFees', label: 'Missing Student Fees' },
        { value: 'outstanding', label: 'Outstanding Invoice' },
        { value: 'judges', label: 'Judges' },
        { value: 'volunteers', label: 'Volunteers' },
        { value: 'staff', label: 'Staff' },
        { value: 'attendees', label: 'Attendees' },
        { value: 'firsttimemissing', label: `${props.Settings.text.ParticipantLabel ?? 'Students'} - Missing first time login` },
        { value: 'tempParticipants', label: `Partially Registered ${props.Settings.text.ParticipantLabel ?? 'Students'}` },
        { value: 'tempNonParticipants', label: `Partially Registered Non-${props.Settings.text.ParticipantLabel ?? 'Students'}` },
        { value: 'SchoolAffiliationParticipantBilling', label:'School Affiliation Participation Billing'},
        { value: 'SchoolAffiliationBilling', label:'School Affiliation Billing'},
      ];
      setRecipientCategories(recipientCategories);
    }
  }, [props.User]);

  useEffect(() => {
    if (errorMessage) {
      toast.error(errorMessage);
      setErrorMessage('');
    }
  }, [errorMessage]);

  useEffect(() => {
    let filteredArray = selectedFiles.reduce((file: any, current: any) => {
      if (current.invalid) return file;
      const x: any = file.find((item: any) => item.name === current.name);
      if (!x) {
        return [...file, current];
      } else {
        return file;
      }
    }, []);
    setValidFiles([...filteredArray]);
  }, [selectedFiles]);

  const mappedRecipients = recipientCategories.map((group) => {
    return {
      value: group.value,
      label: group.label,
    };
  });

  const mappedSchoolList = schoolList.map((school: School) => {
    return {
      value: school.id,
      label: school.name,
    };
  });

  const mappedEntryStatus = () => {
    return [
      { value: `Pending`, label: props.Settings?.text.EntryStatusPending },
      { value: `Approved`, label: props.Settings?.text.EntryStatusApproved },
      { value: `Dropped`, label: props.Settings?.text.EntryStatusDropped },
      { value: `DoNotAdvance`, label: props.Settings?.text.EntryStatusDNA },
    ];
  };

  const onEditorStateChanged = (state) => {
    setPreEmailEditorState(state);
  };

  const toggleHideSchoolList = () => setHideSchoolList(!hideSchoolList);

  const checkEmailRegex = (emailAddress: string) => {
    return Constants.emailRegex.test(emailAddress.toLowerCase());
  };

  const onChange = (e: any) => {
    if (e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const validateFile = (file: any) => {
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    if (validTypes.indexOf(file.type) === -1) {
      return false;
    }
    return true;
  };

  const validateFileSize = (file: any) => {
    const maxSize = 10 * 1024 * 1024; //10MB max size
    console.log('this is the file for size validation', file);
    if (file.size > maxSize) {
      console.log('validateFileSize returning false');
      return false;
    }
    return true;
  };

  const handleFiles = async (files: any) => {
    let filesArr: any[] = [];
    for (let i = 0; i < files.length; i++) {
      if (validateFileSize(files[i])) {
        if (validateFile(files[i])) {
          // add to an array so we can display the name of file
          filesArr.push(files[i]);
        } else {
          // add a new property called invalid
          files[i]['invalid'] = true;
          // do some error stuff
          setErrorMessage('Not a valid format');
        }
      } else {
        files[i]['invalid'] = true;
        await setErrorMessage('Files must be < 10MB');
      }
      setSelectedFiles(filesArr);
    }
  };

  const handleSend = (isPreview: boolean) => {

    if(!isPreview && !window.confirm('Are you sure you want to send this message?')) return;

    if (selectedRecipients.length === 0) return toast.error('Need a "To" group');
    if (subjectVal === '') return toast.error('Need a Subject');
    let messageBody = preEmailEditorState;
    let checkBodyArr = messageBody.split(/[\n\r]/g);
    // the toHtml() in an empty body returns a <p></p> and a new line.
    if (checkBodyArr[0] === `<p></p>` && checkBodyArr.length === 2) return toast.error('Need Email Body content');

    // public int[] personTags { get; set; }
    // public int[] projectTags { get; set; }

    let formattingReqBody = {
      schoolFilter: selectedSchools.map((school) => school.value),
      entryStatusFilter: selectedStatus?.map((x) => x.value),
      isPreview,
      subject: subjectVal,
      message: messageBody,
    };

    if (selectedProjectTags.length > 0) formattingReqBody['projectTags'] = selectedProjectTags.map((x) => x.value);
    if (selectedPersonTags.length > 0) formattingReqBody['personTags'] = selectedPersonTags.map((x) => x.value);

    selectedRecipients.forEach((recipient: { value: string; label: string }) => {
      formattingReqBody[recipient.value] = true;
    });

    let filesArr = validFiles;

    console.log('sending this object', formattingReqBody);

    store.server
      .postApi<ServerResponse<IBulkMessageResults>>('../Email/SendBulkEmail', formattingReqBody, filesArr)
      .then((res) => {
        if (res.Success) {
          console.log(res);
          setMessageResults(res.Value);
          setCurrentMessage(0);
            setShowSendModal(true);

          if (!isPreview) {
            handleAnotherClick();
          }
        } else {
          toast.error(res.Message);
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error(`Error trying to send your message. ${err}`);
      });
  };

  const handleAnotherClick = () => {
    setSelectedRecipients([]);
    setPreEmailEditorState('');
    setHideSchoolList(true);
    setSelectedFiles([]);
    setErrorMessage('');
    setValidFiles([]);
    setSubjectVal('');
    handleClearFilters();
  };

  const handleClearFilters = () => {
    setSelectedSchools([]);
    setSelectedPersonTags([]);
    setSelectedProjectTags([]);
    setSelectedStatus([]);
  };

  const mappedPersonTags = personTags.map((tag) => {
    return {
      label: tag.Value,
      value: tag.Id,
    };
  });

  const mappedProjectTags = projectTags.map((tag) => {
    return {
      label: tag.Value,
      value: tag.Id,
    };
  });

  return (
    <div className="bumper-l">
      <div className="col-sm-12">
        <h3>Bulk Mailer</h3>
        Quickly and easily communicate with your students, teachers, volunteers, and staff members. Fill out the form below and we will send them all an email.
        <br />
        <br />
      </div>
      <div className="form-horizontal">
        <div className="form-group row">
          <label className="control-label col-sm-3">To</label>

          <Select
            onChange={(newValue) => setSelectedRecipients(newValue as Selectable[])}
            value={selectedRecipients}
            options={mappedRecipients}
            className="col-sm-6 grow-1"
            isMulti
            isClearable={true}
            menuPlacement={'auto'}
          />
          <div className="col-sm-3">
            <button className="btn btn-default" onClick={toggleHideSchoolList}>
              <i className="fal fa-filter"></i> Toggle Additional Filters
            </button>
          </div>
        </div>
        <div className="form-group">
          {/* <label className='control-label'>Additional Filters</label> */}

          {!hideSchoolList && (
            <>
              <div className="col-sm-8 col-sm-offset-2 soft-border">
                <h3>Additional Filters</h3>
                <p>
                  Only the people selected in the <strong>To</strong>field and additional filter(s) will be sent this message.
                </p>
                {(props.Settings?.TrackEntryStatus ?? false) && (
                  <div className="row">
                    <label className="control-label col-sm-4">Entry Status</label>
                    <Select
                      onChange={(newValue) => setSelectedStatus(newValue as Selectable[])}
                      value={selectedStatus}
                      options={mappedEntryStatus()}
                      placeholder={'choose none for all entries'}
                      className="col-sm-8 grow-1"
                      isMulti
                      isClearable={true}
                      menuPlacement={'auto'}
                    />
                  </div>
                )}
                <div className="row">
                  <label className="control-label col-sm-4">School Filter</label>
                  <Select
                    onChange={(newValue) => setSelectedSchools(newValue as Selectable[])}
                    value={selectedSchools}
                    options={mappedSchoolList}
                    placeholder={'choose none for all schools'}
                    className="col-sm-8 grow-1"
                    isMulti
                    isClearable={true}
                    menuPlacement={'auto'}
                  />
                </div>
                <div className="row">
                  <label className="control-label col-sm-4">Person Tag Filter</label>
                  {/* <input className='col-sm-10 grow-1' placeholder='person tag filter select here' /> */}
                  <Select
                    onChange={(newValue) => setSelectedPersonTags(newValue as Selectable[])}
                    value={selectedPersonTags}
                    options={mappedPersonTags}
                    placeholder={'Person Tags'}
                    className="col-sm-8 grow-1"
                    isMulti
                    isClearable={true}
                    menuPlacement={'auto'}
                  />
                </div>
                <div className="row">
                  <label className="control-label col-sm-4">Project Tag Filter</label>
                  {/* <input className='col-sm-10 grow-1' placeholder='project tag filter select here' /> */}
                  <Select
                    onChange={(newValue) => setSelectedProjectTags(newValue as Selectable[])}
                    value={selectedProjectTags}
                    options={mappedProjectTags}
                    placeholder={'Project Tags'}
                    className="col-sm-8 grow-1"
                    isMulti
                    isClearable={true}
                    menuPlacement={'auto'}
                  />
                </div>
                <div className="text-right">
                  <button className="btn btn-default" onClick={handleClearFilters}>
                    clear filters
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="form-group row">
          <label className="control-label col-sm-3">Subject</label>
          <div className="col-sm-8">
            <input placeholder={'Email subject'} onChange={(e) => setSubjectVal(e.target.value)} value={subjectVal} className="form-control " />
          </div>
        </div>
        <div className="form-group">
          <div className="col-sm-9 col-sm-offset-3">
            <Editor
              value={preEmailEditorState}
              options={'simple'}
              changed={onEditorStateChanged}
              height={'normal'}
              // placeholder={`Type your message here, below is an example.

              //   Dear $NAME$,

              //   Remember the fair is tomorrow.

              //   Thank you.`}
            />

            <div className="text-muted">
              <h4>Message Keys:</h4>
              These keys will be replaced with the propper data when messages are sent out. Clicking a key will copy it so you can easily paste it into your message. Please note: Message keys are case sensitive and only work for the message and not the subject. 
              <ul>
<li><span className="bold copy" onClick={()=>{navigator.clipboard.writeText('[First-Name]'); toast.info(`Copied key.`)}}>[First-Name]</span> </li>
<li><span className="bold copy" onClick={()=>{navigator.clipboard.writeText('[Last-Name]'); toast.info(`Copied key.`)}}>[Last-Name]</span></li>
<li><span className="bold copy" onClick={()=>{navigator.clipboard.writeText('[Name]'); toast.info(`Copied key.`)}}>[Name]</span> will be replaced with recipients name.</li>
<li><span className="bold copy" onClick={()=>{navigator.clipboard.writeText('[Email]'); toast.info(`Copied key.`)}}>[Email]</span></li>
<li><span className="bold copy" onClick={()=>{navigator.clipboard.writeText('[Sign-In-Link]'); toast.info(`Copied key.`)}}>[Sign-In-Link]</span> this still requireds users to enter their username and password.</li>

{selectedRecipients.find(x=>x.value === 'students') && <>
<li><span className="bold copy" onClick={()=>{navigator.clipboard.writeText('[Entry-Title]'); toast.info(`Copied key.`)}}>[Entry-Title]</span></li>
<li><span className="bold copy" onClick={()=>{navigator.clipboard.writeText('[Entry-Id]'); toast.info(`Copied key.`)}}>[Entry-Id]</span></li>
<li><span className="bold copy" onClick={()=>{navigator.clipboard.writeText('[School]'); toast.info(`Copied key.`)}}>[School]</span></li>
<li><span className="bold copy" onClick={()=>{navigator.clipboard.writeText('[Teacher-First-Name]'); toast.info(`Copied key.`)}}>[Teacher-First-Name]</span></li>
<li><span className="bold copy" onClick={()=>{navigator.clipboard.writeText('[Teacher-Last-Name]'); toast.info(`Copied key.`)}}>[Teacher-Last-Name]</span></li>
<li><span className="bold copy" onClick={()=>{navigator.clipboard.writeText('[Teacher-Email-Name]'); toast.info(`Copied key.`)}}>[Teacher-Email-Name]</span></li>
{props.Settings?.usesParty ? true : false && <li><span className="bold copy" onClick={()=>{navigator.clipboard.writeText('[Party-Member-Info]'); toast.info(`Copied key.`)}}>[Party-Member-Info]</span> will be replaced with a list of their party members. </li>}
</>}

{props.Settings?.showQuickSignInOption ? true: false && <li><span className="bold copy" onClick={()=>{navigator.clipboard.writeText('[Sign-In-Token]'); toast.info(`Copied key.`)}}>[Sign-In-Token]</span></li>}

{selectedRecipients.find(x=>x.value === 'outstanding') && <li><span className="bold copy" onClick={()=>{navigator.clipboard.writeText('[Invoice]'); toast.info(`Copied key.`)}}>[Invoice]</span> will be replaced with link to invoice. (outstanding invoice only)</li>}
{selectedRecipients.find(x=>x.value === 'judges') && <li><span className="bold copy" onClick={()=>{navigator.clipboard.writeText('[Circle-Assignments]'); toast.info(`Copied key.`)}}>[Circle-Assignments]</span> will be replaced with a list of judging circle assignments.</li>}
{selectedRecipients.find(x=>x.value === 'tempParticipants') && <li><span className="bold copy" onClick={()=>{navigator.clipboard.writeText('[Continue-Register-Link]'); toast.info(`Copied key.`)}}>[Continue-Register-Link]</span></li>}

{selectedRecipients.find(x=>x.value === 'SchoolAffiliationParticipantBilling') && <li><span className="bold copy" onClick={()=>{navigator.clipboard.writeText('[Invoice]'); toast.info(`Copied key.`)}}>[Invoice]</span></li>}
{selectedRecipients.find(x=>x.value === 'SchoolAffiliationBilling') && <li><span className="bold copy" onClick={()=>{navigator.clipboard.writeText('[Invoice]'); toast.info(`Copied key.`)}}>[Invoice]</span></li>}

              </ul>
            </div>
          </div>
        </div>
        <div className="bumper-l bumper-out-l form-group row">
          <label className="control-label col-sm-3">Attach Files</label>
          <div className="col-sm-6">
            <input type="file" className="form-control" onChange={onChange} />
          </div>
        </div>
      </div>

      <div className="col-sm-9 col-sm-offset-3">        
        {(
          <button onClick={() => handleSend(true)} className="bumper-l bumper-out-l btn btn-primary">
            <i className="fas fa-paper-plane"></i> View Preview
          </button>
        )}
      </div>
      {showSendModal && messageResults && (
        <Modal setModalOpen={setShowSendModal} title="Message Preview" size='xl'>
          <div>
            <div className='flex-between'>
            {messageResults.IsPreview ? <h3>
                Preview Message
              </h3>
              :
              <h3>
                Messages Sent
                </h3>}

                <div>
                  {currentMessage + 1} of {messageResults.ToList.length}
{"  "}
                  <div className='btn-group'>
                    <button onClick={() => setCurrentMessage(currentMessage - 1)} disabled={currentMessage === 0} className='btn btn-default btn-xs'>
                      <i className='fal fa-arrow-down'></i>
                    </button>
                    <button onClick={() => setCurrentMessage(currentMessage + 1)} disabled={currentMessage === messageResults.ToList.length - 1} className='btn btn-default  btn-xs'>
                      <i className='fal fa-arrow-up'></i>
                    </button>
                  </div>
                </div>
                </div>
            <hr/>
            { currentMessage < (messageResults.ToList.length ?? 0) ? <div>
              <div  className='bulk-preview-details'>
                <span className='bulk-preview-email'>Email: <strong>{messageResults.ToList[currentMessage].Email}</strong></span>
                {messageResults.ToList[currentMessage].AllowTextMessage && <span className='bulk-preview-phone'>Phone: <strong>{messageResults.ToList[currentMessage].Phone}</strong></span>}
                {messageResults.ToList[currentMessage].SentAtUtc && <span className='bulk-preview-sent-at'>Sent At: <strong>{FormatDate(messageResults.ToList[currentMessage].SentAtUtc, 'pretty')}</strong></span>}
              </div>
              <div className='bulk-preview-subject' title='Subject'>{messageResults.ToList[currentMessage].Subject}</div>
              <div  className='bulk-preview-message' dangerouslySetInnerHTML={{__html:messageResults.ToList[currentMessage].Body}}></div>
            </div>:<div>
              <br/>
              <h3>No Results</h3>
              No messages were generated or sent.
              <br/>
              <br/>
              </div>}

            <hr/>
            {messageResults.IsPreview && messageResults.ToList.length > 0 &&<div>
              <button onClick={() => handleSend(false)} className="bumper-l bumper-out-l btn btn-success">
                <i className="fas fa-paper-plane"></i> Send
              </button>
            </div>}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ReduxMap(EmailToolContainer);
